import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchColumns, fetchTables } from '@/api/endpoints';
import { persistNotebook } from '@/api/notebookPersistence';
import { newId, useNotebookStore } from '@/store/notebookStore';
import { useUiStore } from '@/store/uiStore';
import { useResultStore } from '@/query/resultStore';
import { useShortcuts } from '@/hooks/useShortcuts';
import { ObjectExplorer } from '@/components/explorer/ObjectExplorer';
import { ColumnExplorer } from '@/components/columns/ColumnExplorer';
import { QueryHistory } from '@/components/history/QueryHistory';
import { FleetPanel } from '@/components/fleet/FleetPanel';
import { NotebookCell, type NotebookCellHandle } from '@/components/notebook/NotebookCell';
import { CommandPalette, type Command } from '@/components/palette/CommandPalette';
import { UploadDialog } from '@/components/files/UploadDialog';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { EMPTY_FILTER_SORT } from '@/components/filter/filterModel';
import { EMPTY_GROUP } from '@/components/group/groupModel';
import { TitleBar } from './TitleBar';
import type { CatalogSnapshot } from '@/components/editor/catalogCompletion';
import type { RightTab } from '@/store/uiStore';
import type { NotebookDto } from '@/types/api';

const RIGHT_TABS: readonly { id: RightTab; label: string }[] = [
  { id: 'columns', label: 'Columns' },
  { id: 'history', label: 'History' },
  { id: 'fleet', label: 'Fleet' },
];

export function Workbench(): JSX.Element {
  const ui = useUiStore();
  const notebookStore = useNotebookStore();
  const results = useResultStore();

  const notebook = notebookStore.activeNotebook();
  const cells = notebook.cells;
  const activeCellId = notebookStore.activeCellId ?? cells[0]?.id ?? null;
  const activeResult = results.get(activeCellId ?? '');

  const cellRefs = useRef(new Map<string, NotebookCellHandle | null>());
  const [uploadOpen, setUploadOpen] = useState(false);

  /* ------------------------------------------------------------ catalog -- */

  const tables = useQuery({ queryKey: ['catalog', 'tables'], queryFn: () => fetchTables() });
  const columns = useQuery({ queryKey: ['catalog', 'columns'], queryFn: () => fetchColumns() });

  const catalogRef = useRef<CatalogSnapshot>({ tables: [], columns: [] });
  catalogRef.current = { tables: tables.data ?? [], columns: columns.data ?? [] };
  const getCatalog = useCallback((): CatalogSnapshot => catalogRef.current, []);

  /* --------------------------------------------------------- persistence -- */

  const persist = useMutation({
    mutationFn: (dto: NotebookDto) => persistNotebook(dto, () => newId('nb')),
    onSuccess: (result) => {
      // The server rejected the id (another principal's, or deleted); the
      // notebook was saved under a fresh one, so adopt it locally.
      if (result.reassignedFrom) {
        useNotebookStore.getState().reassignNotebookId(result.reassignedFrom, result.saved.id);
      }
    },
  });
  const persistRef = useRef(persist.mutate);
  persistRef.current = persist.mutate;

  useEffect(() => {
    // Debounced write-through. localStorage already has the edit (zustand
    // persist is synchronous), so this is purely the cross-device copy.
    const timer = window.setTimeout(() => {
      persistRef.current(useNotebookStore.getState().toDto());
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [notebook.updatedAt]);

  const saveState = persist.isPending ? 'saving' : persist.isError ? 'offline' : 'saved';

  /* ------------------------------------------------------------- actions -- */

  const runCell = useCallback(
    (cellId: string | null) => {
      if (!cellId) return;
      const target = useNotebookStore.getState().activeNotebook().cells.find((c) => c.id === cellId);
      if (target) void results.run(cellId, target.sql);
    },
    [results],
  );

  const runSelection = useCallback(
    (cellId: string, selection: string) => {
      const sql = selection.trim();
      if (sql === '') {
        runCell(cellId);
        return;
      }
      void results.run(cellId, sql);
    },
    [results, runCell],
  );

  const runAndAdvance = useCallback(
    (cellId: string) => {
      runCell(cellId);
      const index = cells.findIndex((cell) => cell.id === cellId);
      const next = cells[index + 1];
      if (next) {
        notebookStore.setActiveCell(next.id);
        window.setTimeout(() => cellRefs.current.get(next.id)?.focusEditor(), 0);
      } else {
        const created = notebookStore.addCell(cellId);
        window.setTimeout(() => cellRefs.current.get(created)?.focusEditor(), 0);
      }
    },
    [cells, notebookStore, runCell],
  );

  const insertIntoActiveCell = useCallback(
    (text: string) => {
      if (!activeCellId) return;
      const handle = cellRefs.current.get(activeCellId);
      if (handle) {
        handle.insertAtCursor(text);
        return;
      }
      const current = cells.find((cell) => cell.id === activeCellId);
      notebookStore.setCellSql(activeCellId, `${current?.sql ?? ''}${text}`);
    },
    [activeCellId, cells, notebookStore],
  );

  const appendPredicate = useCallback(
    (predicate: string) => {
      insertIntoActiveCell(`\n-- from Column Explorer\n-- WHERE ${predicate}\n`);
    },
    [insertIntoActiveCell],
  );

  const rerunSql = useCallback(
    (sql: string) => {
      const id = notebookStore.addCell(activeCellId ?? undefined);
      notebookStore.setCellSql(id, sql);
      window.setTimeout(() => void results.run(id, sql), 0);
    },
    [activeCellId, notebookStore, results],
  );

  /* ----------------------------------------------------------- shortcuts -- */

  useShortcuts(
    useMemo(
      () => [
        { combo: 'mod+b', run: ui.toggleObjectExplorer, allowInInput: true },
        { combo: 'mod+i', run: ui.toggleColumnExplorer, allowInInput: true },
        { combo: 'mod+e', run: ui.toggleViewMode, allowInInput: true },
        { combo: 'mod+k', run: () => ui.setPaletteOpen(true), allowInInput: true },
        { combo: 'mod+enter', run: () => runCell(activeCellId), allowInInput: false },
        {
          combo: 'mod+arrowup',
          allowInInput: true,
          run: () => {
            if (activeCellId) notebookStore.moveCell(activeCellId, -1);
          },
        },
        {
          combo: 'mod+arrowdown',
          allowInInput: true,
          run: () => {
            if (activeCellId) notebookStore.moveCell(activeCellId, 1);
          },
        },
      ],
      [ui, activeCellId, notebookStore, runCell],
    ),
  );

  const commands = useMemo<Command[]>(
    () => [
      { id: 'run', group: 'Query', label: 'Run cell', keys: '⌘↵', run: () => runCell(activeCellId) },
      {
        id: 'run-advance',
        group: 'Query',
        label: 'Run cell and advance',
        keys: '⇧↵',
        run: () => activeCellId && runAndAdvance(activeCellId),
      },
      {
        id: 'cancel',
        group: 'Query',
        label: 'Cancel running query',
        run: () => activeCellId && results.cancel(activeCellId),
      },
      {
        id: 'add-cell',
        group: 'Notebook',
        label: 'Add cell below',
        run: () => notebookStore.addCell(activeCellId ?? undefined),
      },
      {
        id: 'duplicate',
        group: 'Notebook',
        label: 'Duplicate cell',
        run: () => activeCellId && notebookStore.duplicateCell(activeCellId),
      },
      {
        id: 'delete',
        group: 'Notebook',
        label: 'Delete cell',
        run: () => activeCellId && notebookStore.deleteCell(activeCellId),
      },
      {
        id: 'move-up',
        group: 'Notebook',
        label: 'Move cell up',
        keys: '⌘↑',
        run: () => activeCellId && notebookStore.moveCell(activeCellId, -1),
      },
      {
        id: 'move-down',
        group: 'Notebook',
        label: 'Move cell down',
        keys: '⌘↓',
        run: () => activeCellId && notebookStore.moveCell(activeCellId, 1),
      },
      {
        id: 'new-notebook',
        group: 'Notebook',
        label: 'New notebook',
        run: () => notebookStore.createNotebook(),
      },
      {
        id: 'worksheet',
        group: 'View',
        label: 'Toggle notebook / worksheet',
        keys: '⌘E',
        run: ui.toggleViewMode,
      },
      {
        id: 'objects',
        group: 'View',
        label: 'Toggle Object Explorer',
        keys: '⌘B',
        run: ui.toggleObjectExplorer,
      },
      {
        id: 'columns',
        group: 'View',
        label: 'Toggle Column Explorer',
        keys: '⌘I',
        run: ui.toggleColumnExplorer,
      },
      { id: 'history', group: 'View', label: 'Show query history', run: () => ui.setRightTab('history') },
      { id: 'fleet', group: 'View', label: 'Show duckling fleet', run: () => ui.setRightTab('fleet') },
      {
        id: 'theme',
        group: 'View',
        label: 'Toggle light / dark theme',
        run: ui.toggleTheme,
      },
      {
        id: 'upload',
        group: 'Data',
        label: 'Upload a Parquet file',
        hint: 'registers it as a table',
        run: () => setUploadOpen(true),
      },
    ],
    [activeCellId, notebookStore, results, runAndAdvance, runCell, ui],
  );

  /* -------------------------------------------------------------- render -- */

  const worksheet = ui.viewMode === 'worksheet';
  const visibleCells = worksheet
    ? cells.filter((cell) => cell.id === activeCellId)
    : cells;

  return (
    <div className="flex h-full flex-col bg-base">
      <TitleBar
        notebookName={notebook.name}
        onRename={notebookStore.renameNotebook}
        viewMode={ui.viewMode}
        onToggleViewMode={ui.toggleViewMode}
        theme={ui.theme}
        onToggleTheme={ui.toggleTheme}
        objectExplorerOpen={ui.objectExplorerOpen}
        onToggleObjectExplorer={ui.toggleObjectExplorer}
        columnExplorerOpen={ui.columnExplorerOpen}
        onToggleColumnExplorer={ui.toggleColumnExplorer}
        tier={results.tier}
        onTierChange={results.setTier}
        onOpenPalette={() => ui.setPaletteOpen(true)}
        saveState={saveState}
        notebooks={notebookStore.notebooks}
        activeNotebookId={notebookStore.activeNotebookId}
        onSelectNotebook={notebookStore.selectNotebook}
        onCreateNotebook={notebookStore.createNotebook}
        onRenameNotebook={notebookStore.renameNotebookById}
        onDeleteNotebook={notebookStore.deleteNotebook}
      />

      <div className="flex min-h-0 flex-1">
        {ui.objectExplorerOpen ? (
          <nav
            className="flex w-60 shrink-0 flex-col border-r border-line bg-surface"
            aria-label="Object Explorer"
          >
            <ErrorBoundary label="Object Explorer">
              <ObjectExplorer
                onInsertQuery={insertIntoActiveCell}
                onUpload={() => setUploadOpen(true)}
              />
            </ErrorBoundary>
          </nav>
        ) : null}

        <main className="flex min-w-0 flex-1 flex-col overflow-hidden" aria-label="Notebook">
          {worksheet ? null : (
            <div className="flex h-7 shrink-0 items-center gap-2 border-b border-line bg-surface px-2.5">
              <span className="eyebrow">
                {cells.length} {cells.length === 1 ? 'cell' : 'cells'}
              </span>
              <button
                type="button"
                className="icon-btn text-[11px]"
                onClick={() => notebookStore.addCell(activeCellId ?? undefined)}
              >
                + Cell
              </button>
            </div>
          )}

          <div
            className={
              worksheet
                ? 'flex min-h-0 flex-1 flex-col p-2'
                : 'flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-2'
            }
          >
            {visibleCells.map((cell) => {
              const index = cells.findIndex((entry) => entry.id === cell.id);
              const cellResult = results.get(cell.id);
              return (
                <ErrorBoundary
                  key={cell.id}
                  label={`Cell ${index + 1}`}
                  resetKey={cellResult.queryId ?? cell.id}
                >
                <NotebookCell
                  ref={(handle) => cellRefs.current.set(cell.id, handle)}
                  cellId={cell.id}
                  index={index}
                  total={cells.length}
                  sql={cell.sql}
                  result={results.get(cell.id)}
                  active={cell.id === activeCellId}
                  expanded={worksheet}
                  getCatalog={getCatalog}
                  onSqlChange={(sql) => notebookStore.setCellSql(cell.id, sql)}
                  onFocus={() => notebookStore.setActiveCell(cell.id)}
                  onRun={() => runCell(cell.id)}
                  onRunSelection={(selection) => runSelection(cell.id, selection)}
                  onRunAndAdvance={() => runAndAdvance(cell.id)}
                  onCancel={() => results.cancel(cell.id)}
                  onDelete={() => notebookStore.deleteCell(cell.id)}
                  onDuplicate={() => notebookStore.duplicateCell(cell.id)}
                  onMove={(direction) => notebookStore.moveCell(cell.id, direction)}
                  onAddBelow={() => notebookStore.addCell(cell.id)}
                  onSortChange={(sort) => void results.setSort(cell.id, sort)}
                  onFiltersChange={(filters) => void results.setFilters(cell.id, filters)}
                  onColumnOrderChange={(order) => results.setColumnOrder(cell.id, order)}
                  onToggleColumn={(name) => results.toggleColumn(cell.id, name)}
                  onColumnWidthsChange={(widths) => results.setColumnWidths(cell.id, widths)}
                  onFocusColumn={(name) => ui.setFocusedColumn(name)}
                  onExportParquet={() => results.exportParquet(cell.id)}
                  onExportData={() => results.exportData(cell.id)}
                  filterSort={cell.filterSort ?? EMPTY_FILTER_SORT}
                  onFilterSortChange={(next) => notebookStore.setCellFilterSort(cell.id, next)}
                  onApplyFilterSort={(applied) =>
                    void results.applyFilterSort(
                      cell.id,
                      applied.filters,
                      applied.sort,
                      applied.predicate,
                    )
                  }
                  onLoadDistinct={(column) => results.distinctValues(cell.id, column)}
                  group={cell.group ?? EMPTY_GROUP}
                  onGroupChange={(next) => notebookStore.setCellGroup(cell.id, next)}
                  onApplyGroup={(next) => void results.applyGroup(cell.id, next)}
                />
                </ErrorBoundary>
              );
            })}
          </div>
        </main>

        {ui.columnExplorerOpen ? (
          <aside
            className="flex w-72 shrink-0 flex-col border-l border-line bg-surface"
            aria-label="Column Explorer"
          >
            <div className="flex h-7 shrink-0 items-center border-b border-line" role="tablist">
              {RIGHT_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={ui.rightTab === tab.id}
                  className="eyebrow h-full flex-1 border-b-2 transition-colors"
                  style={{
                    borderColor: ui.rightTab === tab.id ? 'var(--c-local)' : 'transparent',
                    color: ui.rightTab === tab.id ? 'var(--c-ink)' : 'var(--c-faint)',
                  }}
                  onClick={() => ui.setRightTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <ErrorBoundary label={RIGHT_TABS.find((t) => t.id === ui.rightTab)?.label ?? 'Panel'} resetKey={ui.rightTab}>
            {ui.rightTab === 'columns' ? (
              <ColumnExplorer
                columns={activeResult.columns}
                profiles={activeResult.profiles}
                mode={activeResult.mode}
                focusedColumn={ui.focusedColumn}
                onRequestProfile={(column) => {
                  if (activeCellId) void results.loadProfile(activeCellId, column);
                }}
                onFocusColumn={ui.setFocusedColumn}
                onUsePredicate={appendPredicate}
              />
            ) : ui.rightTab === 'history' ? (
              <QueryHistory onRerun={rerunSql} />
            ) : (
              <FleetPanel />
            )}
            </ErrorBoundary>
          </aside>
        ) : null}
      </div>

      {ui.paletteOpen ? (
        <CommandPalette commands={commands} onClose={() => ui.setPaletteOpen(false)} />
      ) : null}

      {uploadOpen ? (
        <UploadDialog
          onClose={() => setUploadOpen(false)}
          onRegistered={(file) => {
            insertIntoActiveCell(`SELECT * FROM ${file.schema}.${file.table} LIMIT 500;`);
            setUploadOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
